import React, { useEffect, useState } from "react";
import { api } from "@/AuthContext";
import { X, FileText, DownloadSimple, Warning } from "@phosphor-icons/react";

export function DocumentViewerInline({ fileId, mimeType, filename, docId, className = "" }) {
  const [url, setUrl] = useState(null);
  const [usesSignedUrl, setUsesSignedUrl] = useState(false);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!fileId && !docId) { setLoading(false); return; }
    let revoke = null;
    let cancelled = false;
    setLoading(true); setErr(null);

    const loadAsBlob = async () => {
      const r = await api.get(`/documents/file/${encodeURIComponent(fileId)}`, { params: { inline: true }, responseType: "blob" });
      const blob = new Blob([r.data], { type: mimeType || r.data.type || "application/octet-stream" });
      const u = URL.createObjectURL(blob);
      revoke = u;
      if (!cancelled) { setUrl(u); setUsesSignedUrl(false); }
    };

    const run = async () => {
      try {
        if (docId) {
          try {
            const r = await api.get(`/documents/${docId}/preview-url`);
            if (r.data?.provider === "gcs" && r.data?.url) {
              if (!cancelled) { setUrl(r.data.url); setUsesSignedUrl(true); }
              return;
            }
          } catch { /* fall through */ }
        }
        await loadAsBlob();
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || "No se pudo cargar el archivo");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; if (revoke) URL.revokeObjectURL(revoke); };
  }, [fileId, mimeType, docId]);

  if (!fileId && !docId) return <Empty msg="Sin archivo asociado"/>;
  if (loading) return <div className={`flex items-center justify-center py-12 text-neutral-400 ${className}`} data-testid="viewer-loading">Cargando previsualización...</div>;
  if (err) return <Empty msg={err} icon={Warning}/>;

  const isPdf = (mimeType || "").includes("pdf") || /\.pdf$/i.test(filename || "");
  const isImage = (mimeType || "").startsWith("image/") || /\.(png|jpe?g|gif|webp|svg|bmp)$/i.test(filename || "");

  if (isPdf) return <iframe src={url} title={filename||"PDF"} className={`w-full h-[70vh] border border-neutral-200 rounded-md bg-neutral-50 ${className}`} data-testid="viewer-pdf"/>;
  if (isImage) return <div className={`bg-neutral-50 border border-neutral-200 rounded-md p-3 flex items-center justify-center ${className}`}><img src={url} alt={filename||""} className="max-w-full max-h-[70vh] object-contain" data-testid="viewer-img"/></div>;

  return (
    <div className={`bg-neutral-50 border border-neutral-200 rounded-md p-8 text-center ${className}`} data-testid="viewer-unsupported">
      <FileText size={42} className="mx-auto text-neutral-400 mb-3" weight="duotone"/>
      <p className="text-sm text-neutral-600 mb-3">Formato no previsualizable en navegador.</p>
      {usesSignedUrl ? (
        <a href={url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-md"><DownloadSimple size={14}/> Abrir</a>
      ) : (
        <a href={url} download={filename} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-md"><DownloadSimple size={14}/> Descargar</a>
      )}
    </div>
  );
}

function Empty({ msg, icon: Icon = FileText }) {
  return <div className="bg-neutral-50 border border-dashed border-neutral-300 rounded-md p-8 text-center text-neutral-400"><Icon size={36} className="mx-auto mb-2" weight="duotone"/><p className="text-sm">{msg}</p></div>;
}

export function DocumentViewerModal({ fileId, mimeType, filename, docId, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") { e.stopImmediatePropagation(); onClose(); } };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="viewer-modal">
      <div className="bg-white rounded-md w-full max-w-5xl border border-neutral-200 max-h-[95vh] overflow-hidden flex flex-col">
        <div className="flex justify-between items-center p-4 border-b border-neutral-200">
          <div className="flex items-center gap-2 min-w-0">
            <FileText size={20} className="text-blue-600 flex-shrink-0" weight="duotone"/>
            <h3 className="font-display font-bold truncate">{filename || "Documento"}</h3>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-neutral-100 rounded" data-testid="viewer-close"><X size={20}/></button>
        </div>
        <div className="p-4 overflow-auto">
          <DocumentViewerInline fileId={fileId} mimeType={mimeType} filename={filename} docId={docId}/>
        </div>
      </div>
    </div>
  );
}
